import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, createContext, useRef } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

import { supabase } from './supabaseClient';
import { useUserEvent } from './hooks';
import { ToastProvider } from './components/Toast';
import UpdateNotification from './components/UpdateNotification';
import Auth from './components/Auth';
import ResetPassword from './components/ResetPassword';
import EmailConfirmation from './components/EmailConfirmation';
import PrevisaoClimatica from './previsao/PrevisaoClimatica';
import Produtividade from './produtividade/Produtividade';
import Pecuaria from './pecuaria/Pecuaria';
import Dashboard from './components/Dashboard';
import Perfil from './components/Perfil';
import MeusPlanos from './components/MeusPlanos';
import AdminPanel from './components/AdminPanel';
import ListaPropriedades from './propriedades/ListaPropriedades';
import Sensores from './sensores';
import Irrigacao from './irrigacao/Irrigacao';
import PagamentoSucesso from './components/PagamentoSucesso';
import PagamentoFalha from './components/PagamentoFalha';
import PagamentoPendente from './components/PagamentoPendente';
import LandingPage from './components/LandingPage';
import TermosDeUso, { VERSAO_TERMOS } from './components/TermosDeUso';
import CompletarPerfil from './components/CompletarPerfil';
import OfflineBanner from './components/OfflineBanner';
import SensoriamentoRemoto from './sensoriamento/SensoriamentoRemoto';
import OperationalAlertsPreview from './components/OperationalAlertsPreview';
import UnifiedAlertsCenter from './components/UnifiedAlertsCenter';
import FieldNotebookPreview from './components/FieldNotebookPreview';
import AuthenticatedBottomNavigation from './components/AuthenticatedBottomNavigation';
import OperationalNotificationsCenter from './components/OperationalNotificationsCenter';
import MigrationReviewLocal from './components/MigrationReviewLocal';
import PlotMigrationWizard from './components/PlotMigrationWizard';
import { canUseLocalRealMigrationData } from './config/plotMigrationAccess';
import { cadernoRuntime, listCadernoCollaborators } from './services/cadernoService';
import { seedLocalOperationalNotifications } from './services/operationalNotificationService';
import { parsePaymentReturn } from './services/paymentReturn';
import { clearInvalidLocalSession, isInvalidRefreshTokenError } from './utils/authSession';
import { resetPageScroll } from './utils/pageScroll';
import { getAuthorizedModuleTarget, getPlanHomeTarget } from './config/modulePermissions';
import { loadVisibleAuthorizedProperties } from './services/authorizedPropertiesService';
import { operationalPhysicalPlotToCompatibleLegacyPlot } from './services/operationalPropertiesService';
import { getAuthenticatedSessionTransition, getAuthBootstrapState } from './utils/authBootstrap';
import CollaborationPanel from './components/CollaborationPanel';
import { CollaborationAccessContext, collaborationEnabled, useCollaborationAccess } from './hooks/useCollaborationAccess';
import { COLLABORATION_CHANGED } from './services/collaborationService';
import {
  haveSameAuthenticatedModules,
  mergeAuthenticatedProfile,
  normalizeAuthenticatedModules,
} from './utils/accountAccessSnapshot';

export const ThemeContext = createContext();

const UNTRACKED_SCREEN_EVENTS = new Set(['alertas', 'caderno', 'notificacoes-operacionais', 'pecuaria', 'colaboracao']);

function AppBootstrapScreen() {
  return (
    <main className="app-bootstrap" role="status" aria-live="polite" aria-label="Preparando sua sessão">
      <div className="app-bootstrap-brand">CX</div>
      <div className="app-bootstrap-copy">
        <strong>Preparando seu ambiente</strong>
        <span>Restaurando a sessão e carregando os dados da sua conta…</span>
      </div>
      <div className="app-bootstrap-progress" aria-hidden="true"><span /></div>
    </main>
  );
}


function MainApp() {
  // Detecta se está rodando como PWA instalado (standalone) ou via Capacitor
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
    window.navigator.standalone === true ||
    Capacitor.isNativePlatform();
  const [session, setSession] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);
  const [mostrarAuth, setMostrarAuth] = useState(isPWA);
  const [tela, setTela] = useState('menu');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [telaSelecionada, setTelaSelecionada] = useState('dashboard');
  const [propriedades, setPropriedades] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isEmailConfirmation, setIsEmailConfirmation] = useState(false);
  const [precisaAceitarTermos, setPrecisaAceitarTermos] = useState(false);
  const [precisaCompletarPerfil, setPrecisaCompletarPerfil] = useState(false);
  const [cadernoSourceAlert, setCadernoSourceAlert] = useState(null);
  const [cadernoFocusOrder, setCadernoFocusOrder] = useState(null);
  const [cadernoLaunchContext, setCadernoLaunchContext] = useState({ action: null, propertyId: null, plotId: null, returnTarget: null, returnContext: null, nonce: 0 });
  const [sensoriamentoLaunchContext, setSensoriamentoLaunchContext] = useState(null);
  const [pecuariaLaunchContext, setPecuariaLaunchContext] = useState(null);
  const [sensoresLaunchContext, setSensoresLaunchContext] = useState(null);
  const [propriedadesLaunchContext, setPropriedadesLaunchContext] = useState(null);
  const [accountSection, setAccountSection] = useState('perfil');
  const [authenticatedModules, setAuthenticatedModules] = useState([]);
  const collaborationAccess = useCollaborationAccess(session?.user?.id);
  const effectiveModules = useMemo(() => [...authenticatedModules, ...collaborationAccess.sharedModules.map(key => `shared:${key}`)], [authenticatedModules, collaborationAccess.sharedModules]);
  const [collaborationInvitation] = useState(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    return params.has('collaboration') ? { id: params.get('collaboration'), token: params.get('invitation') } : null;
  });
  const [authenticatedModulesLoading, setAuthenticatedModulesLoading] = useState(true);
  const [accountAccessNotice, setAccountAccessNotice] = useState('');

  useLayoutEffect(() => {
    resetPageScroll();
  }, [tela]);
  const [fieldContext, setFieldContext] = useState({ loading: false, properties: [], error: '' });
  const [collaborationRevision, setCollaborationRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setCollaborationRevision(version => version + 1);
    window.addEventListener(COLLABORATION_CHANGED, refresh);
    return () => window.removeEventListener(COLLABORATION_CHANGED, refresh);
  }, []);
  // Ref para rastrear o usuário autenticado atual e evitar navegação em token refresh
  const activeUserIdRef = useRef(null);

  // Verificar se é página de reset password ou confirmação de email
  useEffect(() => {
    const checkAuthActions = async () => {
      // Verifica tanto no hash quanto na query string
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const searchParams = new URLSearchParams(window.location.search);
      const type = hashParams.get('type') || searchParams.get('type');
      const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
      // token_hash é usado pelo Supabase v2 no fluxo PKCE (padrão em v2)
      const tokenHash = searchParams.get('token_hash');
      
      console.log('🔍 Verificando URL:', { type, hasToken: !!accessToken, hasTokenHash: !!tokenHash });
      
      const paymentReturn = parsePaymentReturn();
      if (paymentReturn.screen) {
        console.log('✅ Detectado retorno de pagamento:', paymentReturn.screen);
        setTela(paymentReturn.screen);
        return;
      }
      
      // Detecta link de recuperação de senha:
      // - PKCE flow (Supabase v2 padrão): ?token_hash=xxx&type=recovery
      // - Implicit flow (legado): #access_token=xxx&type=recovery
      if (type === 'recovery' || (tokenHash && type !== 'signup') || (accessToken && type !== 'signup' && !type)) {
        console.log('✅ Detectado link de recuperação de senha');
        setIsResetPassword(true);
        // A troca do token por sessão é feita pelo componente ResetPassword
      } 
      // Confirmação de email: ?token_hash=xxx&type=signup  ou  #access_token=xxx&type=signup
      else if (type === 'signup' || (tokenHash && type === 'signup') || (accessToken && type === 'signup')) {
        console.log('✅ Detectado link de confirmação de email');
        setIsEmailConfirmation(true);
      }
    };
    
    checkAuthActions();
  }, []);

  useEffect(() => {
    async function processarConvitesPendentes(email, userId) {
      if (!email || !userId || collaborationEnabled) return;
      // Usa RPC com SECURITY DEFINER para contornar RLS de propriedades_compartilhadas
      const { data: total, error } = await supabase.rpc('processar_convites_pendentes');
      if (error) {
        console.error('[convites] Erro ao processar:', error.message);
      } else if (total > 0) {
        console.log(`[convites] ${total} convite(s) processado(s) para ${email}`);
      }
    }

    async function fetchUsuarioAndSession(sessionObj) {
      const incomingUserId = sessionObj?.user?.id ?? null;
      const transition = getAuthenticatedSessionTransition({
        activeUserId: activeUserIdRef.current,
        incomingUserId,
      });
      activeUserIdRef.current = incomingUserId;
      setSession(sessionObj);
      setAuthResolved(true);
      // O retorno do checkout tem prioridade sobre a navegação padrão da
      // restauração da sessão; caso contrário a cobrança não é reconciliada.
      if (transition.shouldNavigateToDashboard && !parsePaymentReturn().screen) {
        setTela('dashboard');
      }
      if (sessionObj?.user?.id) {
        // TOKEN_REFRESHED e SIGNED_IN repetido para a mesma conta apenas
        // atualizam as credenciais. Recarregar o perfil aqui desmontava o módulo
        // aberto e apagava formulários, filtros e a posição atual do mapa.
        if (!transition.shouldRefreshProfile) return;
        setProfileResolved(false);
        try {
          // Busca o usuário completo da tabela usuarios
          const { data: usuarioDb } = await supabase
            .from('usuarios')
            .select('*')
            .eq('id', sessionObj.user.id)
            .single();
          if (!usuarioDb) {
          // Registro ausente: pode ter ocorrido falha no upsert durante o cadastro.
          // Tenta recriar silenciosamente sem bloquear o acesso.
          const nomeMetadata = sessionObj.user.user_metadata?.nome || sessionObj.user.user_metadata?.full_name || '';
          const { data: novoUsuario } = await supabase
            .from('usuarios')
            .upsert(
              {
                id: sessionObj.user.id,
                email: sessionObj.user.email,
                nome: nomeMetadata,
                telefone: '',
                data_nascimento: null,
              },
              { onConflict: 'id', ignoreDuplicates: true }
            )
            .select()
            .single();

          // Usa o registro recriado ou um objeto mínimo em memória — nunca bloqueia o acesso
          const usuarioFallback = novoUsuario || {
            id: sessionObj.user.id,
            email: sessionObj.user.email,
            nome: nomeMetadata,
            plano: 'starter',
            is_admin: false,
            is_owner: false,
            account_status: 'active',
          };
          if (usuarioFallback.account_status === 'blocked') {
            setAccountAccessNotice('Esta conta foi bloqueada pelo administrador da plataforma. Fale com o suporte para reativar o acesso.');
            await supabase.auth.signOut({ scope: 'local' });
            return;
          }
          setAccountAccessNotice('');
          setUsuario(usuarioFallback);
          setIsAdmin(Boolean(usuarioFallback?.is_admin || usuarioFallback?.is_owner));
          // Novo usuário nunca aceitou os termos — verificar igualmente
          const { data: aceiteNovo } = await supabase
            .from('aceite_termos')
            .select('id')
            .eq('user_id', sessionObj.user.id)
            .eq('versao_termos', VERSAO_TERMOS)
            .maybeSingle();
          if (!aceiteNovo) setPrecisaAceitarTermos(true);
          // Usuário veio de convite — sem nome ainda
          if (!nomeMetadata) setPrecisaCompletarPerfil(true);
          // Processar convites pendentes também para novos usuários (vindos de convite)
            await processarConvitesPendentes(sessionObj.user.email, sessionObj.user.id);
            return;
          }
          if (usuarioDb.account_status === 'blocked') {
            setAccountAccessNotice('Esta conta foi bloqueada pelo administrador da plataforma. Fale com o suporte para reativar o acesso.');
            await supabase.auth.signOut({ scope: 'local' });
            return;
          }
          setAccountAccessNotice('');
          setUsuario({
            ...usuarioDb,
            email: usuarioDb?.email || sessionObj.user.email || '',
          });
          setIsAdmin(Boolean(usuarioDb?.is_admin || usuarioDb?.is_owner));
          // Verifica se usuário já aceitou a versão atual dos termos
          const { data: aceite } = await supabase
            .from('aceite_termos')
            .select('id')
            .eq('user_id', sessionObj.user.id)
            .eq('versao_termos', VERSAO_TERMOS)
            .maybeSingle();
          if (!aceite) setPrecisaAceitarTermos(true);

          // Processar convites pendentes para este email
          await processarConvitesPendentes(sessionObj.user.email, sessionObj.user.id);
        } finally {
          setProfileResolved(true);
        }
      } else {
        setUsuario(null);
        setIsAdmin(false);
        setProfileResolved(true);
      }
    }

    async function carregarSessaoInicial() {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        if (isInvalidRefreshTokenError(error)) {
          await clearInvalidLocalSession(supabase);
          await fetchUsuarioAndSession(null);
          return;
        }
        console.error('Não foi possível restaurar a sessão:', error.message);
      }
      await fetchUsuarioAndSession(data?.session || null);
    }
    carregarSessaoInicial();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // No fluxo PKCE (Supabase v2 padrão) o SDK troca o ?code= por sessão automaticamente
      // e dispara PASSWORD_RECOVERY. Precisamos mostrar a tela de redefinição agora.
      if (_event === 'PASSWORD_RECOVERY') {
        console.log('🔐 Evento PASSWORD_RECOVERY detectado - exibindo tela de redefinição de senha');
        setIsResetPassword(true);
        return;
      }
      // Consultas ao banco dentro do callback podem bloquear o mutex interno do
      // Supabase. Agenda o carregamento para logo após o evento de autenticação.
      window.setTimeout(() => { fetchUsuarioAndSession(nextSession); }, 0);
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user?.id || !Object.prototype.hasOwnProperty.call(usuario || {}, 'account_status')) return undefined;

    let checking = false;
    const verifyAccountAccess = async () => {
      if (checking || document.visibilityState === 'hidden') return;
      checking = true;
      try {
        const [accessResult, profileResult, modulesResult] = await Promise.all([
          supabase.rpc('climorax_current_user_access_state'),
          supabase
            .from('usuarios')
            .select('*')
            .eq('id', session.user.id)
            .maybeSingle(),
          supabase
            .from('user_modules')
            .select('module_name')
            .eq('user_id', session.user.id),
        ]);

        const latestAccountStatus = accessResult.data?.status || profileResult.data?.account_status;
        if (latestAccountStatus === 'blocked') {
          setAccountAccessNotice('Esta conta foi bloqueada pelo administrador da plataforma. Fale com o suporte para reativar o acesso.');
          await supabase.auth.signOut({ scope: 'local' });
          return;
        }

        if (!profileResult.error && profileResult.data) {
          setAccountAccessNotice('');
          setUsuario(current => mergeAuthenticatedProfile(current, profileResult.data, session.user));
          setIsAdmin(Boolean(profileResult.data.is_admin || profileResult.data.is_owner));
        }

        if (!modulesResult.error) {
          const nextModules = normalizeAuthenticatedModules(modulesResult.data);
          setAuthenticatedModules(current => (
            haveSameAuthenticatedModules(current, nextModules) ? current : nextModules
          ));
        }
      } finally {
        checking = false;
      }
    };

    window.addEventListener('focus', verifyAccountAccess);
    const intervalId = window.setInterval(verifyAccountAccess, 30_000);

    return () => {
      window.removeEventListener('focus', verifyAccountAccess);
      window.clearInterval(intervalId);
    };
  }, [session?.user?.email, session?.user?.id, usuario?.account_status]);

  // Registrar abertura do app (lifecycle tracking)
  useEffect(() => {
    if (!usuario?.id) return;

    const registrarAbertura = async () => {
      await supabase.from('log_acessos').insert({
        user_id: usuario.id,
        email: usuario.email,
        data_hora: new Date().toISOString()
      });
    };

    // Registra abertura inicial
    registrarAbertura();

    // Listener para quando app volta do background (nativo Capacitor)
    let listenerHandle;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && usuario?.id) {
          // Log de acesso
          await supabase.from('log_acessos').insert({
            user_id: usuario.id,
            email: usuario.email,
            data_hora: new Date().toISOString()
          });
        }
      }).then(handle => {
        listenerHandle = handle;
      });
    }

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [usuario?.id]);

  // Configurar StatusBar para dispositivos móveis
  useEffect(() => {
    const configureStatusBar = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Define a barra de status como light-content (ícones brancos) para fundos escuros
          await StatusBar.setStyle({ style: Style.Light });
          
          // Define a cor de fundo da barra de status para combinar com o header
          await StatusBar.setBackgroundColor({ color: darkMode ? '#232526' : '#1976d2' });
          
          // Mostra a barra de status
          await StatusBar.show();
          
          // Sobrepõe o conteúdo - vamos adicionar padding no header manualmente
          await StatusBar.setOverlaysWebView({ overlay: true });
        } catch (error) {
          console.log('Erro ao configurar StatusBar:', error);
        }
      }
    };
    
    configureStatusBar();
  }, [darkMode]);

  useEffect(() => {
    async function fetchPropriedades() {
      if (!usuario?.id) {
        setPropriedades([]);
        return;
      }
      const { data } = await supabase
        .from('propriedades')
        .select('id, nome, municipio, estado, area, proprietario, observacoes, user_id')
        .eq('user_id', usuario.id);

      // Corrige caso venha [{}]
      if (Array.isArray(data) && data.length === 1 && Object.keys(data[0]).length === 0) {
        setPropriedades([]);
      } else {
        setPropriedades(data || []);
      }
    }
    fetchPropriedades();
  }, [usuario?.id]);

  useEffect(() => {
    if (!usuario?.id || fieldContext.loading || fieldContext.error || fieldContext.properties.length === 0) return;
    seedLocalOperationalNotifications({
      userId: usuario.id,
      userName: usuario.nome || usuario.email || 'Usuário autenticado',
      properties: fieldContext.properties,
    });
  }, [usuario?.id, usuario?.nome, usuario?.email, fieldContext]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAuthenticatedModules() {
      if (!usuario?.id) {
        setAuthenticatedModules([]);
        setAuthenticatedModulesLoading(false);
        return;
      }

      setAuthenticatedModulesLoading(true);

      const { data, error } = await supabase
        .from('user_modules')
        .select('module_name')
        .eq('user_id', usuario.id);

      if (!cancelled) {
        setAuthenticatedModules(error ? [] : normalizeAuthenticatedModules(data));
        setAuthenticatedModulesLoading(false);
      }
    }

    fetchAuthenticatedModules();
    return () => { cancelled = true; };
  }, [usuario?.id]);

  useEffect(() => {
    let cancelled = false;

    async function fetchFieldContext() {
      if (!usuario?.id) {
        setFieldContext({ loading: false, properties: [], error: '' });
        return;
      }

      setFieldContext(current => ({ ...current, loading: true, error: '' }));

      try {
        const { properties: accessibleProperties } = await loadVisibleAuthorizedProperties(
          usuario.id,
          'id, nome, municipio, estado, area, user_id'
        );
        const legacyPropertyIds = accessibleProperties
          .filter(item => !item._usesPhysicalPlots)
          .map(item => item.id)
          .filter(Boolean);
        let plots = [];
        let cadernoCollaborators = [];

        if (legacyPropertyIds.length > 0) {
          const plotsResult = await supabase
            .from('talhoes')
            .select('id, nome, cultura, area, propriedade_id, data_plantio, data_colheita')
            .in('propriedade_id', legacyPropertyIds)
            .order('nome');

          if (plotsResult.error) throw plotsResult.error;
          plots = plotsResult.data || [];

        }
        const propertyIds = accessibleProperties.map(item => item.id).filter(Boolean);
        if (propertyIds.length > 0 && cadernoRuntime.readsEnabled) {
          cadernoCollaborators = await listCadernoCollaborators(propertyIds);
        }

        const normalizedProperties = accessibleProperties.map(property => {
          const permission = property._accessRole || (property.user_id === usuario.id ? 'owner' : 'viewer');
          const secureCollaborators = cadernoCollaborators
            .filter(collaborator => String(collaborator.propertyId) === String(property.id));
          const currentUserFallback = permission === 'owner' || permission === 'editor'
            ? [{
              id: usuario.id,
              name: usuario.nome || 'Usuário autenticado',
              permission,
              role: permission === 'owner' ? 'Proprietário' : 'Editor',
              isCurrentUser: true,
            }]
            : [];
          const collaborators = [...secureCollaborators, ...currentUserFallback]
            .filter((collaborator, index, items) => (
              items.findIndex(item => String(item.id) === String(collaborator.id)) === index
            ));
          const legacyPlots = plots.filter(plot => String(plot.propriedade_id) === String(property.id));
          const canonicalPlots = property._usesPhysicalPlots
            ? (property._operationalPlots || []).map(plot => (
                operationalPhysicalPlotToCompatibleLegacyPlot(plot, legacyPlots, property)
              ))
            : legacyPlots;

          return {
            id: property.id,
            name: property.nome,
            municipio: property.municipio,
            estado: property.estado,
            location: [property.municipio, property.estado].filter(Boolean).join(', '),
            area: property.area,
            permission,
            collaborationPermissions: property._collaborationPermissions,
            collaborators,
            plots: canonicalPlots
              .map(plot => ({
                id: plot.id,
                name: plot.nome,
                culture: plot.cultura,
                area: plot.area,
                plantingDate: plot.data_plantio,
                harvestDate: plot.data_colheita,
                _physicalPlotId: plot._physicalPlotId || null,
                _legacyPlotIds: plot._legacyPlotIds || [plot.id],
              })),
          };
        });

        if (!cancelled) {
          setFieldContext({ loading: false, properties: normalizedProperties, error: '' });
        }
      } catch (error) {
        if (!cancelled) {
          setFieldContext({
            loading: false,
            properties: [],
            error: error?.message || 'Não foi possível carregar o contexto da conta.',
          });
        }
      }
    }

    fetchFieldContext();
    return () => { cancelled = true; };
  }, [usuario?.id, collaborationRevision]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Erro ao sair do Supabase:', e);
    } finally {
      setSession(null);
      setAuthResolved(true);
      setProfileResolved(true);
      setUsuario(null);
      setIsAdmin(false);
      setTela('menu'); // volta para tela inicial/login
    }
  };

  const authenticatedNavigationScreens = ['dashboard', 'alertas', 'caderno', 'notificacoes-operacionais', 'propriedades', 'previsao', 'produtividade', 'sensores', 'irrigacao', 'sensoriamento', 'perfil', 'planos', 'admin', 'colaboracao', 'pecuaria'];
  const authenticatedHomeTarget = getPlanHomeTarget(usuario?.plano, isAdmin);

  const canAccessAuthenticatedModule = useCallback((target) => {
    if (target === 'admin') return Boolean(isAdmin);
    return getAuthorizedModuleTarget(target, effectiveModules, isAdmin, usuario?.plano) === target;
  }, [effectiveModules, isAdmin, usuario?.plano]);

  useEffect(() => {
    if (!collaborationInvitation) return;
    setMostrarAuth(true);
    if (session && profileResolved) setTela('colaboracao');
  }, [collaborationInvitation, session?.user?.id, profileResolved]);

  useEffect(() => {
    if (!session || !profileResolved || authenticatedModulesLoading) return;
    const protectedScreen = authenticatedNavigationScreens.includes(tela)
      && tela !== 'admin';
    if (protectedScreen && !canAccessAuthenticatedModule(tela)) setTela(authenticatedHomeTarget);
  }, [authenticatedHomeTarget, authenticatedModulesLoading, canAccessAuthenticatedModule, profileResolved, session, tela]);

  const navigateAuthenticatedModule = (target, context = {}) => {
    if (!authenticatedModulesLoading && !canAccessAuthenticatedModule(target)) {
      setTela(authenticatedHomeTarget);
      return;
    }
    if (target === 'caderno') {
      setCadernoSourceAlert(context.fromAlert || null);
      setCadernoFocusOrder(context.orderId || null);
      setCadernoLaunchContext({
        action: context.action || null,
        propertyId: context.propertyId || null,
        plotId: context.plotId || null,
        returnTarget: context.returnTarget || null,
        returnContext: context.returnContext || null,
        nonce: Date.now(),
      });
    }
    if (target === 'sensoriamento') {
      setSensoriamentoLaunchContext(context && Object.keys(context).length > 0
        ? { ...context, nonce: Date.now() }
        : null);
    }
    if (target === 'pecuaria') setPecuariaLaunchContext({ ...context, nonce: Date.now() });
    if (target === 'sensores') {
      setSensoresLaunchContext(context && Object.keys(context).length > 0
        ? { ...context, nonce: Date.now() }
        : null);
    }
    if (target === 'propriedades') {
      setPropriedadesLaunchContext(context && Object.keys(context).length > 0
        ? { ...context, nonce: Date.now() }
        : null);
    }
    if (target === 'perfil') {
      setAccountSection(context.section || 'perfil');
    }
    setTela(target);
  };

  const updateAuthenticatedTheme = (nextValue) => {
    setDarkMode(currentValue => {
      const resolvedValue = typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;
      localStorage.setItem('darkMode', resolvedValue);
      return resolvedValue;
    });
  };
  const showAuthenticatedNavigation = Boolean(session && authenticatedNavigationScreens.includes(tela));
  // Telas cujos componentes não registram o próprio uso.
  useUserEvent({
    userId: usuario?.id,
    tela: session && UNTRACKED_SCREEN_EVENTS.has(tela) ? tela : null,
    evento: 'page_view',
    comDuração: true,
  });
  const authBootstrapState = getAuthBootstrapState({
    authResolved,
    hasSession: Boolean(session),
    profileResolved,
  });
  // isAdmin agora vem do estado, atualizado após login

  if (session?.user && tela === 'previsao') {
    // previsao render
  }

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode: updateAuthenticatedTheme }}>
      <CollaborationAccessContext.Provider value={collaborationAccess}>
      <ToastProvider>
        <OfflineBanner />
        {/* Página de confirmação de email */}
        {isEmailConfirmation && (
          <EmailConfirmation 
            onConfirmed={() => {
              setIsEmailConfirmation(false);
              setTela('dashboard');
            }} 
          />
        )}
        
        {/* Página de redefinição de senha */}
        {isResetPassword && <ResetPassword />}
        
        {/* Modal de Termos de Uso — exibido quando necessário */}
        {session && precisaAceitarTermos && (
          <TermosDeUso
            userId={session.user.id}
            onAceito={() => setPrecisaAceitarTermos(false)}
          />
        )}

        {/* Modal de completar perfil — usuários vindos de convite sem nome */}
        {session && !precisaAceitarTermos && precisaCompletarPerfil && (
          <CompletarPerfil
            userId={session.user.id}
            email={session.user.email}
            onConcluido={(nomeNovo) => {
              setUsuario(u => u ? { ...u, nome: nomeNovo } : u);
              setPrecisaCompletarPerfil(false);
            }}
          />
        )}

        {/* App normal */}
        {!isResetPassword && !isEmailConfirmation && authBootstrapState.startsWith('loading') && (
          <AppBootstrapScreen />
        )}
        {!isResetPassword && !isEmailConfirmation && !authBootstrapState.startsWith('loading') && (
          <>
            {session && <UpdateNotification />}
            <div className={showAuthenticatedNavigation ? 'authenticated-app-frame' : ''} style={{ minHeight: '100vh' }}>
              { !session && !mostrarAuth && (
                <LandingPage
                  onEntrar={() => setMostrarAuth(true)}
                  onCadastrar={() => setMostrarAuth(true)}
                />
              )}
              { !session && mostrarAuth && <Auth notice={accountAccessNotice} /> }
          { tela === 'previsao' && (
            session?.user ? (
              <PrevisaoClimatica
                onBack={() => setTela('dashboard')}
                user={session.user}
                onNavigate={navigateAuthenticatedModule}
                operationalProperties={fieldContext.properties}
              />
            ) : (
              <div style={{ textAlign: 'center', color: '#1976d2', fontWeight: 600, padding: 40 }}>
                Carregando usuário...
              </div>
            )
          ) }
          { session && tela === 'produtividade' && <Produtividade onBack={() => setTela('dashboard')} user={usuario} onNavigate={navigateAuthenticatedModule} pecuariaEnabled={canAccessAuthenticatedModule('pecuaria')} /> }
          { session && tela === 'pecuaria' && canAccessAuthenticatedModule('pecuaria') && (
            <Pecuaria key={pecuariaLaunchContext?.nonce || 'pecuaria'} onBack={() => setTela(authenticatedHomeTarget)} onNavigate={navigateAuthenticatedModule} initialContext={pecuariaLaunchContext} darkMode={darkMode} mapEnabled={canAccessAuthenticatedModule('sensoriamento')} />
          )}
          { session && tela === 'dashboard' && (
            <Dashboard
              user={usuario}
              onSelecionarTela={navigateAuthenticatedModule}
              onOpenAlerts={() => navigateAuthenticatedModule('alertas')}
              onOpenFieldNotebook={() => navigateAuthenticatedModule('caderno')}
              telaSelecionada={telaSelecionada}
              onLogout={handleLogout}
              onAbrirPropriedades={() => navigateAuthenticatedModule('propriedades')}
              allowedModules={effectiveModules}
              isAdmin={isAdmin}
              integratedNavigation
            />
          )}
          { session && tela === 'alertas' && (
            <UnifiedAlertsCenter
              userId={usuario?.id || session.user.id}
              darkMode={darkMode}
              setDarkMode={updateAuthenticatedTheme}
              onNavigate={navigateAuthenticatedModule}
            />
          )}
          { session && tela === 'caderno' && (
            <FieldNotebookPreview
              key={`authenticated-${cadernoSourceAlert || 'manual'}-${cadernoFocusOrder || 'lista'}-${cadernoLaunchContext.nonce}`}
              darkMode={darkMode}
              setDarkMode={updateAuthenticatedTheme}
              scenario="operacao"
              evaluationMode
              sourceAlertId={cadernoSourceAlert}
              focusOrderId={cadernoFocusOrder}
              initialAction={cadernoLaunchContext.action}
              initialPropertyId={cadernoLaunchContext.propertyId}
              initialPlotId={cadernoLaunchContext.plotId}
              returnTarget={cadernoLaunchContext.returnTarget}
              returnContext={cadernoLaunchContext.returnContext}
              accountLabel={usuario?.nome ? usuario.nome.split(' ')[0] : 'Conta autenticada'}
              currentUserId={usuario?.id || session?.user?.id}
              currentUserName={usuario?.nome || usuario?.email || 'Usuário autenticado'}
              realProperties={fieldContext.properties}
              realContextLoading={fieldContext.loading}
              realContextError={fieldContext.error}
              pecuariaEnabled={canAccessAuthenticatedModule('pecuaria')}
              onNavigate={navigateAuthenticatedModule}
            />
          )}
          { session && tela === 'notificacoes-operacionais' && (
            <OperationalNotificationsCenter
              darkMode={darkMode}
              setDarkMode={updateAuthenticatedTheme}
              currentUserId={usuario?.id || session?.user?.id}
              currentUserName={usuario?.nome || usuario?.email || 'Usuário autenticado'}
              realProperties={fieldContext.properties}
              realContextLoading={fieldContext.loading}
              realContextError={fieldContext.error}
              onNavigate={navigateAuthenticatedModule}
            />
          )}
          { session && tela === 'propriedades' && (
            <ListaPropriedades
              key={`propriedades-${propriedadesLaunchContext?.nonce || 'manual'}`}
              user={usuario}
              onVoltarDashboard={() => setTela('dashboard')}
              onNavigate={navigateAuthenticatedModule}
              initialContext={propriedadesLaunchContext}
            />
          )}
          { session && tela === 'perfil' && (
            <Perfil
              user={usuario}
              authenticatedUser={session?.user || null}
              authIdentityLoading={!authResolved}
              initialSection={accountSection}
              onOpenPlans={() => setTela('planos')}
              onBack={() => setTela('dashboard')}
            />
          )}
          { session && tela === 'planos' && <MeusPlanos user={usuario} onBack={() => setTela('dashboard')} /> }
          { session && tela === 'colaboracao' && <CollaborationPanel user={{ ...usuario, email: session.user.email }} invitation={collaborationInvitation} darkMode={darkMode} onNavigate={navigateAuthenticatedModule} onBack={() => setTela(authenticatedHomeTarget)} /> }
          { isAdmin && tela === 'admin' && <AdminPanel currentUser={usuario} onBack={() => setTela('dashboard')} /> }
          { session && tela === 'sensores' && <Sensores onBack={() => setTela('dashboard')} onNavigate={navigateAuthenticatedModule} user={usuario} initialContext={sensoresLaunchContext} /> }
          { session && tela === 'irrigacao' && <Irrigacao onBack={() => setTela('dashboard')} user={usuario} /> }
          { session && tela === 'sensoriamento' && canAccessAuthenticatedModule('sensoriamento') && <SensoriamentoRemoto onBack={() => setTela('dashboard')} onNavigate={navigateAuthenticatedModule} user={usuario} initialContext={sensoriamentoLaunchContext} pecuariaEnabled={canAccessAuthenticatedModule('pecuaria')} /> }
          {showAuthenticatedNavigation && (
            <AuthenticatedBottomNavigation
              activeScreen={tela}
              darkMode={darkMode}
              setDarkMode={updateAuthenticatedTheme}
              user={usuario}
              allowedModules={effectiveModules}
              onNavigate={navigateAuthenticatedModule}
              onLogout={handleLogout}
            />
          )}

          {/* Rotas de Pagamento */}
          { tela === 'pagamento-sucesso' && <PagamentoSucesso onVoltar={() => setTela('perfil')} /> }
          { tela === 'pagamento-falha' && <PagamentoFalha onVoltar={() => setTela('dashboard')} /> }
          { tela === 'pagamento-pendente' && <PagamentoPendente onVoltar={() => setTela('perfil')} /> }
            </div>
          </>
        )}
      </ToastProvider>
      </CollaborationAccessContext.Provider>
    </ThemeContext.Provider>
  );
}

function DashboardPreview({ darkMode, setDarkMode, previewScenario, onNavigate }) {
  const operationActive = previewScenario === 'operacao';
  const previewInsights = {
    loading: false,
    activeAlerts: operationActive ? 2 : 0,
    lastSyncAt: operationActive ? new Date(Date.now() - 12 * 60 * 1000).toISOString() : null,
    propertyCount: operationActive ? 2 : 0,
    sensorCount: operationActive ? 3 : 0,
    activeTalhoesCount: operationActive ? 5 : 0,
  };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <Dashboard
        user={{ nome: 'Usuário Demo', is_admin: false }}
        onSelecionarTela={onNavigate}
        onOpenAlerts={() => onNavigate('alertas')}
        onOpenFieldNotebook={() => onNavigate('caderno')}
        telaSelecionada="dashboard"
        onLogout={() => onNavigate('dashboard')}
        onAbrirPropriedades={() => onNavigate('propriedades')}
        previewInsights={previewInsights}
        allowedModules={['propriedades', 'previsao', 'sensores', 'irrigacao', 'sensoriamento']}
      />
    </ThemeContext.Provider>
  );
}

function PropriedadesPreview({ darkMode, setDarkMode, previewScenario, onNavigate, launchContext = null }) {
  const previewData = useMemo(() => {
    const propriedades = [
      {
        id: 'property-horizonte', nome: 'Fazenda Horizonte', municipio: 'Três Lagoas', estado: 'MS',
        area: 62, proprietario: 'Usuário de demonstração', coordenada_x: -51.5302, coordenada_y: -20.8242,
        observacoes: 'Área principal de produção e monitoramento climático.',
      },
      {
        id: 'property-boa-vista', nome: 'Sítio Boa Vista', municipio: 'Viçosa', estado: 'MG',
        area: 28.5, proprietario: 'Família Filgueiras', coordenada_x: -42.8796, coordenada_y: -20.7547,
        observacoes: 'Unidade destinada a testes agronômicos e acompanhamento de solo.',
      },
    ];

    return {
      propriedades,
      permissoes: { 'property-horizonte': 'owner', 'property-boa-vista': 'owner' },
      talhoesPorPropriedade: {
        'property-horizonte': [
          {
            id: 'plot-north', propriedade_id: 'property-horizonte', nome: 'Talhão Norte', area: 18.4,
            cultura: 'Eucalipto', data_plantio: '2025-10-18', data_colheita: null,
            operationalStatus: 'critical', waterPct: 28, ndvi: 0.72, ndviChange: 8.3,
            stage: 'Crescimento ativo', age: '9 meses', cycleProgress: 54, lastReading: '18 min',
            sensorName: 'Estação Campo Norte', imageDate: '09 ago', nextAction: 'Irrigar 12,6 mm até 14h',
            recommendationDetail: 'O armazenamento de água está abaixo do nível recomendado. Priorize a irrigação antes do período de maior vento.',
          },
          {
            id: 'plot-central', propriedade_id: 'property-horizonte', nome: 'Talhão Central', area: 12.8,
            cultura: 'Eucalipto', data_plantio: '2025-11-04', data_colheita: null,
            operationalStatus: 'watch', waterPct: 42, ndvi: 0.68, ndviChange: 3.1,
            stage: 'Desenvolvimento', age: '8 meses', cycleProgress: 48, lastReading: '21 min',
            sensorName: 'Estação Campo Norte', imageDate: '09 ago', nextAction: 'Planejar reposição de 8,2 mm',
            recommendationDetail: 'A umidade está próxima do limite de manejo. Reavalie a leitura no início da tarde antes de programar a irrigação.',
          },
          {
            id: 'plot-south', propriedade_id: 'property-horizonte', nome: 'Talhão Sul', area: 21.2,
            cultura: 'Eucalipto', data_plantio: '2026-01-12', data_colheita: null,
            operationalStatus: 'stable', waterPct: 78, ndvi: 0.76, ndviChange: 5.4,
            stage: 'Estabelecimento', age: '7 meses', cycleProgress: 39, lastReading: '26 min',
            sensorName: 'Chuva · Setor Sul', imageDate: '09 ago', nextAction: 'Manter acompanhamento',
            recommendationDetail: 'Água disponível e vigor vegetativo em condição adequada. Não há necessidade de irrigação neste momento.',
          },
        ],
        'property-boa-vista': [
          {
            id: 'plot-coffee', propriedade_id: 'property-boa-vista', nome: 'Talhão Café', area: 8.3,
            cultura: 'Café', data_plantio: '2024-09-20', data_colheita: null,
            operationalStatus: 'watch', waterPct: 46, ndvi: 0.64, ndviChange: -1.8,
            stage: 'Produção', age: '1 ano e 10 meses', cycleProgress: 71, lastReading: '34 min',
            sensorName: 'Umidade · Talhão Café', imageDate: '07 ago', nextAction: 'Vistoriar setor leste',
            recommendationDetail: 'A imagem mais recente indica pequena redução de vigor no setor leste. Confirme em campo antes de intervir.',
          },
          {
            id: 'plot-trial', propriedade_id: 'property-boa-vista', nome: 'Área Experimental', area: 4.6,
            cultura: 'Milho', data_plantio: '2026-02-08', data_colheita: null,
            operationalStatus: 'stable', waterPct: 69, ndvi: 0.81, ndviChange: 6.2,
            stage: 'Enchimento de grãos', age: '6 meses', cycleProgress: 82, lastReading: '41 min',
            sensorName: 'Estação Boa Vista', imageDate: '07 ago', nextAction: 'Manter acompanhamento',
            recommendationDetail: 'O talhão apresenta boa disponibilidade hídrica e vigor uniforme. Continue o monitoramento diário.',
          },
        ],
      },
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <ListaPropriedades
        key={`${previewScenario}-${launchContext?.nonce || 'manual'}`}
        user={{ nome: 'Usuário Demo' }}
        onVoltarDashboard={() => onNavigate('dashboard')}
        onNavigate={onNavigate}
        previewMode
        previewData={previewScenario === 'operacao' ? previewData : null}
        initialContext={launchContext}
      />
    </ThemeContext.Provider>
  );
}

function SensoresPreview({ darkMode, setDarkMode, previewScenario, onNavigate, initialContext = null }) {
  const previewData = useMemo(() => {
    const now = new Date();
    const buildReadings = (deviceId, makeReading) => Array.from({ length: 12 }, (_, index) => {
      const timestamp = new Date(now.getTime() - index * 2 * 60 * 60 * 1000).toISOString();
      return { id: `${deviceId}-${index}`, device_id: deviceId, timestamp, ...makeReading(index) };
    });

    const sensores = [
      { id: 'sensor-meteo-01', device_id: 'cx-meteo-001', nome_sensor: 'Estação Campo Norte', tipo_sensor: 'meteorologico', latitude: -20.8239, longitude: -51.5301 },
      { id: 'sensor-soil-01', device_id: 'cx-solo-020', nome_sensor: 'Umidade · Talhão Norte', tipo_sensor: 'umidade_solo', profundidade_cm: 20, latitude: -20.8262, longitude: -51.5228 },
      { id: 'sensor-rain-01', device_id: 'cx-chuva-003', nome_sensor: 'Chuva · Setor Sul', tipo_sensor: 'chuva_presenca', latitude: -20.8310, longitude: -51.5300 },
    ];

    return {
      sensores,
      dadosSensores: {
        'cx-meteo-001': buildReadings('cx-meteo-001', index => ({
          temp_c: 26.4 - index * 0.35,
          rh_perc: 63 + index * 1.4,
          rain_mm: index === 5 ? 2.8 : 0,
          wind_ms: 2.1 + (index % 3) * 0.25,
        })),
        'cx-solo-020': buildReadings('cx-solo-020', index => ({
          umidade_solo_perc: 34.8 + index * 0.55,
          temp_solo: 23.6 - index * 0.12,
        })),
        'cx-chuva-003': buildReadings('cx-chuva-003', index => ({
          molhado: index < 2 ? 1 : 0,
        })),
      },
      diasEstiagem: 3,
      alertas: [],
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <ToastProvider>
        <Sensores
          key={previewScenario}
          user={{ nome: 'Usuário Demo' }}
          onBack={() => onNavigate('dashboard')}
          onNavigate={onNavigate}
          previewMode
          previewData={previewScenario === 'operacao' ? previewData : null}
          initialContext={initialContext}
        />
      </ToastProvider>
    </ThemeContext.Provider>
  );
}

function PrevisaoPreview({ darkMode, setDarkMode, previewScenario, onNavigate }) {
  const operationActive = previewScenario === 'operacao';
  const baseDate = new Date();
  baseDate.setHours(12, 0, 0, 0);
  const locationName = operationActive ? 'Três Lagoas' : 'Viçosa';
  const previewData = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + index);
    return {
      date: `${date.toISOString().slice(0, 10)}T12:00:00`,
      name_muni: locationName,
      tmax: (operationActive ? [31, 32, 29, 27, 28, 30, 29] : [27, 28, 26, 25, 27, 29, 28])[index],
      tmean: (operationActive ? [25, 26, 24, 22, 23, 25, 24] : [21, 22, 21, 20, 22, 23, 22])[index],
      tmin: (operationActive ? [20, 21, 20, 18, 19, 20, 19] : [16, 17, 17, 16, 17, 18, 17])[index],
      ur: [72, 68, 76, 81, 74, 66, 70][index],
      rg: [18, 20, 16, 14, 19, 21, 20][index],
      ws2m: [2.1, 2.4, 1.8, 1.6, 2.2, 2.8, 2.3][index],
      ppt: [0, 2.4, 8.6, 12.2, 1.8, 0, 0.6][index],
      eto_mm_day: [3.7, 4.1, 3.2, 2.8, 3.8, 4.4, 4.0][index],
    };
  });
  const hourlyStart = new Date();
  hourlyStart.setMinutes(0, 0, 0);
  let accumulatedRain = 0;
  const previewHourlyData = Array.from({ length: 96 }, (_, index) => {
    const timestamp = new Date(hourlyStart.getTime() + index * 60 * 60 * 1000);
    const localHour = timestamp.getHours();
    const daylight = Math.max(0, Math.sin(((localHour - 6) / 12) * Math.PI));
    const rain = (index >= 25 && index <= 29) || (index >= 67 && index <= 70)
      ? [0.4, 1.2, 2.8, 1.6, 0.5][index >= 67 ? index - 67 : index - 25] || 0.3
      : 0;
    accumulatedRain += rain;

    return {
      datetime_utc: timestamp.toISOString(),
      TMP: 18.5 + daylight * 11 + Math.sin(index / 5) * 0.8,
      RH: Math.max(32, 84 - daylight * 48 + rain * 4),
      APCP: accumulatedRain,
      WS2M: 1.2 + daylight * 2.6 + (index % 5) * 0.08,
      DSWRF: daylight * 780,
      PRES: 91500 + Math.sin(index / 8) * 180,
    };
  });
  const previewMunicipios = [
    { name_muni: 'Viçosa', code_state: 31 },
    { name_muni: 'Patos de Minas', code_state: 31 },
    { name_muni: 'Três Lagoas', code_state: 50 },
    { name_muni: 'Ribas do Rio Pardo', code_state: 50 },
  ];

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <ToastProvider>
        <PrevisaoClimatica
          user={{ nome: 'Usuário Demo' }}
          onBack={() => onNavigate('dashboard')}
          previewMode
          previewData={previewData}
          previewHourlyData={previewHourlyData}
          previewMunicipios={previewMunicipios}
          previewInitialMunicipio={operationActive ? 'Três Lagoas' : null}
          previewOperational={operationActive}
          onNavigate={onNavigate}
          operationalProperties={operationActive ? [{ nome: 'Fazenda Horizonte', municipio: 'Três Lagoas' }] : []}
        />
      </ToastProvider>
    </ThemeContext.Provider>
  );
}

function IrrigacaoPreview({ darkMode, setDarkMode, onNavigate }) {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const toDate = date => date.toISOString().slice(0, 10);

  const estacoes = [
    {
      id: 'station-field-01',
      nome: 'Estação Campo Norte',
      tipo_fonte: 'sensor_proprio',
      device_id: 'CX-METEO-001',
      ativa: true,
      tem_vento: true,
      tem_radiacao: true,
    },
    {
      id: 'station-nasa-01',
      nome: 'NASA POWER · Setor Sul',
      tipo_fonte: 'nasapower',
      latitude: -20.7834,
      longitude: -51.7002,
      altitude: 320,
      ativa: true,
    },
  ];

  const talhaoBase = [
    { id: 'plot-north', nome: 'Talhão Norte', area: 18.4, cultura: 'Eucalipto', estacaoId: 'station-field-01' },
    { id: 'plot-central', nome: 'Talhão Central', area: 12.8, cultura: 'Eucalipto', estacaoId: 'station-field-01' },
    { id: 'plot-riverside', nome: 'Talhão Beira Rio', area: 9.6, cultura: 'Eucalipto', estacaoId: 'station-nasa-01' },
    { id: 'plot-south', nome: 'Talhão Sul', area: 21.2, cultura: 'Eucalipto', estacaoId: 'station-nasa-01' },
  ];

  const talhoes = talhaoBase.map(item => ({
    id: item.id,
    nome: item.nome,
    area: item.area,
    cultura: item.cultura,
    configuracao_irrigacao: [{
      manejo_ativo: true,
      fonte_dados: item.estacaoId === 'station-nasa-01' ? 'nasapower' : 'estacao',
      estacao_id: item.estacaoId,
      sistema_irrigacao: 'Gotejamento',
      eficiencia_sistema: 90,
      capacidade_agua_disponivel: 88,
      cad_por_cm_solo: 1.25,
      kc_meio: 1.05,
      zr_inicial: 25,
      zr_desenvolvimento: 45,
      zr_meio: 70,
      zr_final: 70,
    }],
  }));

  const balancos = [
    {
      id: 'balance-north', nome: 'Talhão Norte', area: 18.4, cultura: 'Eucalipto',
      armazenamentoPercentual: 28, necessidadeMm: 12.6, recomendacao: 'urgente',
      janela: 'Até 14h', tempoAplicacao: '3h 10min de aplicação', fonte: 'Campo Norte',
    },
    {
      id: 'balance-central', nome: 'Talhão Central', area: 12.8, cultura: 'Eucalipto',
      armazenamentoPercentual: 42, necessidadeMm: 8.2, recomendacao: 'irrigar',
      janela: 'Hoje à tarde', tempoAplicacao: '2h 05min de aplicação', fonte: 'Campo Norte',
    },
    {
      id: 'balance-riverside', nome: 'Talhão Beira Rio', area: 9.6, cultura: 'Eucalipto',
      armazenamentoPercentual: 61, necessidadeMm: 3.4, recomendacao: 'aguardar',
      janela: 'Reavaliar amanhã', tempoAplicacao: 'Aguardar nova leitura', fonte: 'NASA POWER',
    },
    {
      id: 'balance-south', nome: 'Talhão Sul', area: 21.2, cultura: 'Eucalipto',
      armazenamentoPercentual: 78, necessidadeMm: 0, recomendacao: 'ok',
      janela: 'Sem irrigação', tempoAplicacao: 'Solo em nível adequado', fonte: 'NASA POWER',
    },
  ];

  const previewData = {
    propriedade: {
      id: 'property-preview',
      nome: 'Fazenda Horizonte',
      municipio: 'Três Lagoas',
      estado: 'MS',
    },
    resumo: {
      total_talhoes: 4,
      talhoes_manejo_ativo: 4,
      talhoes_urgente: 1,
      talhoes_irrigar: 1,
      talhoes_aguardar: 1,
      talhoes_ok: 1,
      estacoes_ativas: 2,
    },
    estacoes,
    talhoes,
    balancos,
    eventos: [
      {
        id: 'event-preview-01',
        talhao_id: 'plot-north',
        talhoes: { nome: 'Talhão Norte' },
        status: 'pendente',
        tipo_evento: 'Irrigação planejada',
        data_planejada: toDate(today),
        lamina_planejada_mm: 12.6,
        tempo_planejado_horas: 3.2,
        motivo: 'Déficit hídrico crítico',
      },
      {
        id: 'event-preview-02',
        talhao_id: 'plot-central',
        talhoes: { nome: 'Talhão Central' },
        status: 'pendente',
        tipo_evento: 'Irrigação planejada',
        data_planejada: toDate(tomorrow),
        lamina_planejada_mm: 8.2,
        tempo_planejado_horas: 2.1,
        motivo: 'Reposição preventiva',
      },
    ],
  };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <ToastProvider>
        <Irrigacao
          user={{ nome: 'Usuário Demo' }}
          onBack={() => onNavigate('dashboard')}
          previewMode
          previewData={previewData}
        />
      </ToastProvider>
    </ThemeContext.Provider>
  );
}

function SensoriamentoPreview({ darkMode, setDarkMode, onNavigate, initialContext = null }) {

  const geometria = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-51.5332, -20.8217],
        [-51.5291, -20.8208],
        [-51.5261, -20.8238],
        [-51.5277, -20.8276],
        [-51.5321, -20.8271],
        [-51.5342, -20.8242],
        [-51.5332, -20.8217],
      ]],
    },
  };

  const geometriaSul = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-51.5320, -20.8280],
        [-51.5278, -20.8283],
        [-51.5264, -20.8320],
        [-51.5301, -20.8340],
        [-51.5334, -20.8318],
        [-51.5320, -20.8280],
      ]],
    },
  };

  const geometriaPivo = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-51.5255, -20.8240],
        [-51.5218, -20.8234],
        [-51.5198, -20.8262],
        [-51.5222, -20.8291],
        [-51.5256, -20.8279],
        [-51.5255, -20.8240],
      ]],
    },
  };

  const geometriaPropriedade = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-51.5361, -20.8197],
        [-51.5190, -20.8200],
        [-51.5175, -20.8310],
        [-51.5297, -20.8360],
        [-51.5365, -20.8322],
        [-51.5361, -20.8197],
      ]],
    },
  };

  const cenas = [
    {
      id: 'S2B-preview-20260809',
      properties: { datetime: '2026-08-09T13:42:00Z', 'eo:cloud_cover': 6.2 },
      links: [{ rel: 'self', href: 'https://preview.climorax.local/scenes/2026-08-09' }],
      assets: {},
    },
    {
      id: 'S2A-preview-20260730',
      properties: { datetime: '2026-07-30T13:39:00Z', 'eo:cloud_cover': 11.8 },
      links: [{ rel: 'self', href: 'https://preview.climorax.local/scenes/2026-07-30' }],
      assets: {},
    },
    {
      id: 'S2B-preview-20260720',
      properties: { datetime: '2026-07-20T13:43:00Z', 'eo:cloud_cover': 3.6 },
      links: [{ rel: 'self', href: 'https://preview.climorax.local/scenes/2026-07-20' }],
      assets: {},
    },
  ];

  const talhao = {
    id: 'remote-plot-north',
    nome: 'Talhão Norte',
    area: 18.4,
    cultura: 'Eucalipto',
    data_plantio: '2025-10-18',
    coordenada_x: -51.5302,
    coordenada_y: -20.8242,
    geometria,
  };

  const talhoes = [
    talhao,
    {
      id: 'remote-plot-south',
      nome: 'Talhão Sul',
      area: 22.7,
      cultura: 'Eucalipto',
      data_plantio: '2025-11-03',
      coordenada_x: -51.5301,
      coordenada_y: -20.8311,
      geometria: geometriaSul,
    },
    {
      id: 'remote-plot-pivot',
      nome: 'Pivô Central',
      area: 14.2,
      cultura: 'Milho',
      data_plantio: '2026-02-12',
      coordenada_x: -51.5228,
      coordenada_y: -20.8262,
      geometria: geometriaPivo,
    },
  ];

  const restoredPreviewTalhao = talhoes.find(item => String(item.id) === String(initialContext?.plotId)) || talhao;
  const propriedadePrincipal = {
    id: 'remote-property-preview',
    nome: 'Fazenda Horizonte',
    municipio: 'Três Lagoas',
    estado: 'MS',
    area: 68.5,
    geometria: geometriaPropriedade,
    _accessRole: 'owner',
  };
  const propriedadeLegada = {
    id: 'remote-property-legacy',
    nome: 'Fazenda Boa Esperança',
    municipio: 'Três Lagoas',
    estado: 'MS',
    area: 42,
    coordenada_x: -51.5452,
    coordenada_y: -20.8294,
    geometria: null,
    _accessRole: 'owner',
  };
  const previewData = {
    propriedade: propriedadePrincipal,
    propriedades: [propriedadePrincipal, propriedadeLegada],
    talhao: restoredPreviewTalhao,
    talhoes,
    talhoesPorPropriedade: {
      [propriedadePrincipal.id]: talhoes,
      [propriedadeLegada.id]: [],
    },
    sensores: [
      {
        id: 'sensor-meteo-01',
        device_id: 'cx-meteo-001',
        nome_sensor: 'Estação Campo Norte',
        tipo_sensor: 'meteorologico',
        latitude: -20.8239,
        longitude: -51.5301,
        rainLast24h: 2.8,
        latestReading: {
          timestamp: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
          temp_c: 28.4,
          rh_perc: 63.2,
          rain_mm: 0,
        },
      },
      {
        id: 'sensor-soil-01',
        device_id: 'cx-solo-020',
        nome_sensor: 'Umidade · Talhão Norte',
        tipo_sensor: 'umidade_solo',
        latitude: -20.8262,
        longitude: -51.5228,
        latestReading: {
          timestamp: new Date(Date.now() - 9 * 60 * 1000).toISOString(),
          umidade_solo_perc: 34.8,
          temp_solo: 23.6,
        },
      },
    ],
    cenas,
    statsNdvi: {
      mean: 0.72,
      std: 0.12,
      min: 0.31,
      max: 0.91,
      histogram: [[15, 0, 70, 15], [0.2, 0.45, 0.62, 0.78, 0.95]],
    },
    temporalComparison: {
      status: 'sucesso',
      atual: 0.72,
      anterior: 0.665,
      delta: 0.055,
      changePct: 8.3,
      dataAtual: '09 ago. 2026',
      dataAnterior: '30 jul. 2026',
    },
    insights: {
      ndvi: 0.72,
      changePct: 8.3,
      comparisonLabel: '30 jul. 2026 → 09 ago. 2026',
      attentionPct: 14.7,
      attentionArea: '2,7 ha',
      imageDate: '09 ago',
      cloudPct: 6.2,
      culture: 'Eucalipto',
      area: '18,4 ha',
      recommendation: 'Vistoriar o setor noroeste',
      recommendationDetail: 'A região apresentou vigor abaixo do restante do talhão em duas imagens consecutivas.',
    },
  };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <SensoriamentoRemoto
        user={{ nome: 'Usuário Demo' }}
        onBack={() => onNavigate('dashboard')}
        onNavigate={onNavigate}
        previewMode
        previewData={previewData}
        initialContext={initialContext}
      />
    </ThemeContext.Provider>
  );
}

function AlertasOperacionaisPreview({ darkMode, setDarkMode, previewScenario, onNavigate }) {
  return (
    <OperationalAlertsPreview
      darkMode={darkMode}
      setDarkMode={setDarkMode}
      scenario={previewScenario}
      onNavigate={onNavigate}
    />
  );
}

function CadernoCampoPreview({ darkMode, setDarkMode, previewScenario, onNavigate, launchContext = null }) {
  return (
    <FieldNotebookPreview
      key={`${previewScenario}-${new URLSearchParams(window.location.search).get('fromAlert') || 'manual'}`}
      darkMode={darkMode}
      setDarkMode={setDarkMode}
      scenario={previewScenario}
      onNavigate={onNavigate}
      focusOrderId={launchContext?.orderId || null}
      initialAction={launchContext?.action || null}
      initialPropertyId={launchContext?.propertyId || null}
      initialPlotId={launchContext?.plotId || null}
      returnTarget={launchContext?.returnTarget || null}
      returnContext={launchContext?.returnContext || null}
    />
  );
}

const PREVIEW_TARGETS = ['dashboard', 'alertas', 'caderno', 'propriedades', 'sensores', 'previsao', 'irrigacao', 'sensoriamento', 'migracao', 'migracao-cliente'];

function PreviewWorkspace() {
  const readPreviewTarget = () => {
    const target = new URLSearchParams(window.location.search).get('preview');
    if (PREVIEW_TARGETS.includes(target)) return target;
    return canUseLocalRealMigrationData() ? 'migracao-cliente' : 'dashboard';
  };

  const [previewTarget, setPreviewTarget] = useState(readPreviewTarget);
  const [previewScenario, setPreviewScenarioState] = useState(
    () => new URLSearchParams(window.location.search).get('scenario') === 'operacao' ? 'operacao' : 'onboarding'
  );
  const [darkMode, setDarkModeState] = useState(
    () => new URLSearchParams(window.location.search).get('theme') === 'dark'
  );
  const [previewLaunchContext, setPreviewLaunchContext] = useState(null);

  const setDarkMode = useCallback((nextValue) => {
    setDarkModeState(currentValue => {
      const resolvedValue = typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;
      const url = new URL(window.location.href);
      if (resolvedValue) url.searchParams.set('theme', 'dark');
      else url.searchParams.delete('theme');
      window.history.replaceState({}, '', url);
      return resolvedValue;
    });
  }, []);

  const navigatePreview = useCallback((target, context = {}) => {
    const resolvedTarget = target === 'migracao-talhoes' ? 'migracao-cliente' : target;
    if (!PREVIEW_TARGETS.includes(resolvedTarget)) return;

    const url = new URL(window.location.href);
    setPreviewLaunchContext(context && Object.keys(context).length > 0
      ? { ...context, nonce: Date.now() }
      : null);
    url.searchParams.set('preview', resolvedTarget);
    if (resolvedTarget === 'caderno' && context.fromAlert) url.searchParams.set('fromAlert', context.fromAlert);
    else url.searchParams.delete('fromAlert');
    window.history.pushState({}, '', url);
    setPreviewTarget(resolvedTarget);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleBrowserNavigation = () => {
      setPreviewTarget(readPreviewTarget());
      setPreviewScenarioState(
        new URLSearchParams(window.location.search).get('scenario') === 'operacao' ? 'operacao' : 'onboarding'
      );
    };
    window.addEventListener('popstate', handleBrowserNavigation);
    return () => window.removeEventListener('popstate', handleBrowserNavigation);
  }, []);

  const sharedProps = { darkMode, setDarkMode, previewScenario, onNavigate: navigatePreview };
  const localRealMigrationMode = previewTarget === 'migracao-cliente'
    && canUseLocalRealMigrationData();
  let previewScreen = <DashboardPreview {...sharedProps} />;

  if (previewTarget === 'alertas') previewScreen = <AlertasOperacionaisPreview {...sharedProps} />;
  if (previewTarget === 'caderno') previewScreen = <CadernoCampoPreview {...sharedProps} launchContext={previewLaunchContext} />;
  if (previewTarget === 'propriedades') previewScreen = <PropriedadesPreview {...sharedProps} launchContext={previewLaunchContext} />;
  if (previewTarget === 'sensores') previewScreen = <SensoresPreview {...sharedProps} initialContext={previewLaunchContext} />;
  if (previewTarget === 'previsao') previewScreen = <PrevisaoPreview {...sharedProps} />;
  if (previewTarget === 'irrigacao') previewScreen = <IrrigacaoPreview {...sharedProps} />;
  if (previewTarget === 'sensoriamento') previewScreen = <SensoriamentoPreview {...sharedProps} initialContext={previewLaunchContext} />;
  if (previewTarget === 'migracao') previewScreen = <MigrationReviewLocal darkMode={darkMode} />;
  if (previewTarget === 'migracao-cliente') previewScreen = (
    <PlotMigrationWizard
      darkMode={darkMode}
      currentUserName={localRealMigrationMode ? 'Revisão interna' : 'Produtor piloto'}
      previewMode={!localRealMigrationMode}
      localReviewMode={localRealMigrationMode}
    />
  );

  const previewActiveScreen = previewTarget === 'migracao' || previewTarget === 'migracao-cliente'
    ? 'migracao-talhoes'
    : previewTarget;

  return (
    <div className={`preview-workspace ${darkMode ? 'is-dark' : ''}`}>
      <div className="preview-screen authenticated-app-frame preview-product-shell">{previewScreen}</div>
      <AuthenticatedBottomNavigation
        activeScreen={previewActiveScreen}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        user={{ nome: 'Usuário Demo', email: 'demo@climorax.local' }}
        allowedModules={['propriedades', 'previsao', 'sensores', 'sensoriamento', 'irrigacao']}
        plotMigrationEnabled
        onNavigate={navigatePreview}
        onLogout={() => {}}
      />
    </div>
  );
}

function App() {
  const requestedPreviewTarget = new URLSearchParams(window.location.search).get('preview');
  const previewTarget = process.env.NODE_ENV === 'development' || requestedPreviewTarget === 'migracao-cliente'
    ? requestedPreviewTarget
    : null;

  if (previewTarget && PREVIEW_TARGETS.includes(previewTarget)) return <PreviewWorkspace />;
  return <MainApp />;
}

export default App;
